import { useState } from "react";
import { Download, Loader2, CheckCircle2, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { downloadFile } from "../../lib/api";
import CvPaper, { DESIGNS } from "./CvPaper";
import { StepNav } from "./Stepper";
import { useCvPhoto, PhotoToggle } from "../app/CvPhoto";

const DESIGN_KEY = "gi_cv_design";

export function DesignPicker({ value, onChange }) {
  return (
    <div role="radiogroup" aria-label="CV design" data-testid="cv-design-picker" className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
      {DESIGNS.map((d) => (
        <button key={d.id} role="radio" aria-checked={value === d.id} data-testid={`template-${d.id}`} onClick={() => onChange(d.id)}
          className={`text-left rounded-xl border px-3.5 py-3 min-h-[64px] transition-colors ${value === d.id ? "border-brand bg-brand-soft ring-1 ring-brand" : "border-line bg-surface hover:border-brand/40"}`}>
          <div className="text-[15px] font-semibold text-ink flex items-center gap-1.5">{value === d.id && <CheckCircle2 className="w-4 h-4 text-brand" />}{d.name}</div>
          <div className="text-sm text-body leading-snug">{d.hint}</div>
        </button>
      ))}
    </div>
  );
}

export default function StepDownload({ app, onUpdate, onBack, onNext }) {
  const [design, setDesign] = useState(() => localStorage.getItem(DESIGN_KEY) || "classic");
  const [dl, setDl] = useState(null);
  const [askApplied, setAskApplied] = useState(false);
  const photo = useCvPhoto();
  const pick = (d) => { setDesign(d); localStorage.setItem(DESIGN_KEY, d); };
  const name = app.company || "application";

  const get = async (kind) => {
    const map = {
      pdf: [`/applications/${app.id}/cv.pdf?template=${design}${photo.query}`, `CV_${name}.pdf`],
      docx: [`/applications/${app.id}/cv.docx?template=${design}${photo.query}`, `CV_${name}.docx`],
      cl: [`/applications/${app.id}/cover-letter.pdf`, `CoverLetter_${name}.pdf`],
      "cl-docx": [`/applications/${app.id}/cover-letter.docx`, `CoverLetter_${name}.docx`],
    };
    setDl(kind);
    try {
      await downloadFile(...map[kind]);
      toast.success("Downloaded. Check your downloads folder.");
      if (app.status === "tailored") setAskApplied(true);
    } catch {
      toast.error("The download didn't work. Please try again.");
    } finally {
      setDl(null);
    }
  };

  const markApplied = async () => {
    try {
      await api.patch(`/applications/${app.id}/status`, { status: "applied" });
      onUpdate({ status: "applied" });
      toast.success("Marked as applied. Good luck!");
      onNext();
    } catch {
      toast.error("Couldn't update the status. Please try again.");
    }
  };

  const Btn = ({ kind, primary, children, testid }) => (
    <button data-testid={testid} onClick={() => get(kind)} disabled={dl === kind} className={`gi-btn ${primary ? "gi-btn-primary" : "gi-btn-secondary"}`}>
      {dl === kind ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} {children}
    </button>
  );

  return (
    <div className="af-fade-up space-y-5" data-testid="studio-step-download">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ink">Preview and download</h1>
        <p className="text-body mt-2">Pick a design. They all use standard headings that ATS software can read.</p>
      </div>
      <DesignPicker value={design} onChange={pick} />
      <div className="grid lg:grid-cols-[1fr_280px] gap-5 items-start">
        <CvPaper cv={app.tailored_cv || {}} design={design} photo={photo.picture} />
        <div className="gi-card p-5 space-y-3 lg:sticky lg:top-24">
          <div className="font-semibold text-ink">Your CV</div>
          <PhotoToggle photo={photo} testid="download-cv-photo" />
          <div className="flex flex-col gap-2">
            <Btn kind="pdf" primary testid="download-cv-pdf-button">Download PDF</Btn>
            <Btn kind="docx" testid="download-cv-docx-button">Download Word</Btn>
          </div>
          {app.cover_letter && (
            <>
              <div className="pt-3 border-t border-line font-semibold text-ink">Cover letter</div>
              <div className="flex flex-col gap-2">
                <Btn kind="cl" testid="download-cover-letter-button">Cover letter PDF</Btn>
                <Btn kind="cl-docx" testid="download-cover-letter-docx-button">Cover letter Word</Btn>
              </div>
            </>
          )}
        </div>
      </div>
      {askApplied && (
        <div className="gi-card border-brand/30 bg-brand-soft/60 p-5 flex flex-wrap items-center gap-3" role="status" data-testid="mark-applied-prompt">
          <CheckCircle2 className="w-5 h-5 text-success" />
          <span className="text-[15px] text-ink flex-1 min-w-[200px]">Downloaded! Have you sent your application yet?</span>
          <button data-testid="mark-applied-yes" onClick={markApplied} className="gi-btn gi-btn-primary gi-btn-sm">Yes, mark as applied</button>
          <button data-testid="mark-applied-later" onClick={() => setAskApplied(false)} className="gi-btn gi-btn-secondary gi-btn-sm">Not yet</button>
        </div>
      )}
      {app.cover_letter && (
        <details className="gi-card p-5 sm:p-6 group" data-testid="cover-letter-preview">
          <summary className="cursor-pointer list-none flex items-center justify-between gap-3">
            <span className="font-display text-lg font-bold text-ink flex items-center gap-2"><FileText className="w-5 h-5 text-brand" /> Read your cover letter</span>
            <span className="text-sm text-brand group-open:hidden">Show</span><span className="text-sm text-brand hidden group-open:inline">Hide</span>
          </summary>
          <div className="mt-4 rounded-xl bg-canvas border border-line p-5 text-[15px] text-body whitespace-pre-wrap leading-relaxed">{app.cover_letter}</div>
        </details>
      )}
      <StepNav onBack={onBack} onNext={onNext} nextLabel="Next: track this application" />
    </div>
  );
}
